// src/components/SystemRecommendation/components/AlternativesConsidered.tsx

import React from 'react';
import { Alternative, ViewMode } from '../types';

interface AlternativesConsideredProps {
  alternatives: Alternative[];
  viewMode: ViewMode;
}

const AlternativesConsidered: React.FC<AlternativesConsideredProps> = ({
  alternatives,
  viewMode
}) => {
  const getAlternativeIcon = (systemType: string) => {
    if (systemType.includes('heat-pump')) return '⚠️';
    return '❌';
  };

  const getAlternativeTitle = (alt: Alternative) => {
    if (viewMode === 'customer') {
      const titles: Record<string, string> = {
        'combi': 'What About A Combi Boiler?',
        'heat-pump-air': 'What About A Heat Pump?',
        'heat-pump-ground': 'What About A Ground Source Heat Pump?',
        'regular-boiler': 'What About A Regular Boiler?'
      };
      return titles[alt.systemType] || alt.systemName;
    }
    return alt.systemName;
  };

  const formatPoints = (alt: Alternative) => {
    if (viewMode === 'customer') {
      // Split into pros and cons for customer view
      const cons = alt.points.filter(p => p.includes('⚠️') || p.includes('higher') || p.includes('need'));
      const pros = alt.points.filter(p => !cons.includes(p));

      return { pros, cons };
    }
    return { pros: [], cons: alt.points };
  };

  return (
    <div className="alternatives-considered">
      <h2 className="section-title">
        {viewMode === 'customer' ? 'Other Options We Considered' : 'Alternatives Considered'}
      </h2>

      <div className="alternatives-list">
        {alternatives.map((alt, index) => {
          const { pros, cons } = formatPoints(alt);

          return (
            <div key={index} className="alternative-card">
              <div className="alternative-header">
                <span className="alternative-icon">
                  {getAlternativeIcon(alt.systemType)}
                </span>
                <h3 className="alternative-title">
                  {getAlternativeTitle(alt)}
                </h3>
              </div>

              <div className="alternative-content">
                <p className="not-recommended-reason">
                  {viewMode === 'customer' && alt.systemType.includes('heat-pump')
                    ? alt.systemName + " could work for your home."
                    : alt.notRecommendedReason}
                </p>

                {viewMode === 'customer' && pros.length > 0 && (
                  <ul className="alternative-pros">
                    {pros.map((pro, i) => (
                      <li key={i} className="pro-point">
                        <span className="pro-icon">✓</span>
                        {pro}
                      </li>
                    ))}
                  </ul>
                )}

                <ul className="alternative-points">
                  {cons.map((point, i) => (
                    <li key={i} className="con-point">
                      {viewMode === 'technical' && '• '}
                      {viewMode === 'customer' && <span className="con-icon">⚠️</span>}
                      {point}
                    </li>
                  ))}
                </ul>

                {alt.couldWorkIf && (
                  <p className="could-work">
                    <strong>Could work if:</strong> {alt.couldWorkIf}
                  </p>
                )}

                {alt.revisitIf && viewMode === 'technical' && (
                  <p className="revisit">
                    <strong>Revisit if:</strong> {alt.revisitIf}
                  </p>
                )}

                {viewMode === 'customer' && alt.systemType.includes('heat-pump') && (
                  <p className="customer-closing">
                    {alt.systemType === 'heat-pump-air'
                      ? "It's a great option if you're planning to stay long-term and want the greenest solution. We can talk through this in detail if you're interested."
                      : "We can discuss this option further if you'd like to explore it."}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AlternativesConsidered;
